import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PageWrapper, Card, Button } from "../components";
import { 
  ArrowLeft, ArrowRight, Check, Sparkles, Volume2,
  Play, Pause, Download, RotateCcw, ChevronRight,
  Headphones, Zap, Sun, Moon, CloudMoon, Brain, Share2
} from "lucide-react";
import { ShareInstallationCard } from "../components/ShareInstallationCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Binaural beat icons mapping
const BINAURAL_ICONS = {
  none: Volume2,
  beta: Zap,
  alpha: Sun,
  theta: Moon,
  delta: CloudMoon,
  gamma: Sparkles,
};

export const CreateInstallationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preloadedText = searchParams.get("text");
  const preloadedCategory = searchParams.get("category");
  const challengeId = searchParams.get("challenge");
  const duplicateMode = searchParams.get("duplicate") === "true";
  const preloadedStyle = searchParams.get("style");
  const preloadedBackground = searchParams.get("background");
  const preloadedBinaural = searchParams.get("binaural");
  
  const audioRef = useRef(null);
  const bgAudioRef = useRef(null);
  const textareaRef = useRef(null);

  const [step, setStep] = useState(1); // 1-5 for wizard, 6 for generating, 7 for complete
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data
  const [categories, setCategories] = useState([]);
  const [affirmations, setAffirmations] = useState([]);
  const [voiceStyles, setVoiceStyles] = useState({});
  const [backgroundOptions, setBackgroundOptions] = useState({});
  const [binauralOptions, setBinauralOptions] = useState({});
  const [challengePreset, setChallengePreset] = useState(null);

  // Selections
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAffirmation, setSelectedAffirmation] = useState(null);
  const [customText, setCustomText] = useState(preloadedText || "");
  const [useCustom, setUseCustom] = useState(!!preloadedText);
  const [selectedStyle, setSelectedStyle] = useState(preloadedStyle || "calm_meditation");
  const [selectedBackground, setSelectedBackground] = useState(preloadedBackground || "silence");
  const [selectedBinaural, setSelectedBinaural] = useState(preloadedBinaural || "none");

  // Generated audio
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loopMode, setLoopMode] = useState("single");
  const [previewingBg, setPreviewingBg] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [playStartTime, setPlayStartTime] = useState(null);

  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // If coming from a challenge, load preset
      if (challengeId) {
        const presetRes = await fetch(`${API_URL}/api/voice-studio/challenge-preset/${challengeId}`);
        if (presetRes.ok) {
          const presetData = await presetRes.json();
          setChallengePreset(presetData);
          setCustomText(presetData.affirmation_text || "");
          setUseCustom(true);
          setSelectedStyle(presetData.suggested_settings?.voice_style || "calm_meditation");
          setSelectedBackground(presetData.suggested_settings?.background_audio || "silence");
          setSelectedBinaural(presetData.suggested_settings?.binaural_beat || "none");
          setVoiceStyles(presetData.voice_styles || {});
          setBackgroundOptions(presetData.background_audio || {});
          setBinauralOptions(presetData.binaural_beats || {});
          setLoading(false);
          return;
        }
      }

      const [catsRes, stylesRes, bgRes, binauralRes] = await Promise.all([
        fetch(`${API_URL}/api/voice-studio/categories`),
        fetch(`${API_URL}/api/voice-studio/voice-styles`),
        fetch(`${API_URL}/api/voice-studio/background-audio`),
        fetch(`${API_URL}/api/voice-studio/binaural-beats`)
      ]);

      const [catsData, stylesData, bgData, binauralData] = await Promise.all([
        catsRes.json(),
        stylesRes.json(),
        bgRes.json(),
        binauralRes.json()
      ]);

      setCategories(catsData.categories || []);
      setVoiceStyles(stylesData.styles || {});
      setBackgroundOptions(bgData.options || {});
      setBinauralOptions(binauralData.options || {});

      if (preloadedCategory) {
        const affsRes = await fetch(`${API_URL}/api/voice-studio/categories/${preloadedCategory}/affirmations`);
        const affsData = await affsRes.json();
        setAffirmations(affsData.affirmations || []);
        setSelectedCategory(preloadedCategory);
      }
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAffirmations = async (categoryId) => {
    try {
      const res = await fetch(`${API_URL}/api/voice-studio/categories/${categoryId}/affirmations`);
      const data = await res.json();
      setAffirmations(data.affirmations || []);
    } catch (e) {
      console.error("Error fetching affirmations:", e);
    }
  };

  const handleCategorySelect = async (category) => {
    setSelectedCategory(category.id);
    setSelectedAffirmation(null);
    await fetchAffirmations(category.id);
  };

  const handleGenerate = async () => {
    const text = useCustom ? customText : selectedAffirmation?.text;
    if (!text) {
      setError("Please enter or select an affirmation");
      return;
    }

    setStep(6); // Generating
    setError(null);

    const authToken = getToken();
    try {
      const res = await fetch(`${API_URL}/api/voice-studio/generate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          affirmation_text: text,
          voice_style: selectedStyle,
          background_audio: selectedBackground,
          binaural_beat: selectedBinaural,
          save_to_library: true
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || "Failed to generate installation");
      }

      setGeneratedAudio(data);
      setStep(7); // Complete
    } catch (e) {
      console.error("Generation error:", e);
      setError(e.message);
      setStep(5); // Back to review
    }
  };

  // Handle audio ended event for timed loops
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      if (loopMode === "single") {
        setIsPlaying(false);
      } else if (loopMode === "continuous") {
        // Already handled by loop=true
      } else {
        // For timed modes (5min, 10min), restart if within time limit
        const durationMs = loopMode === "5min" ? 5 * 60 * 1000 : 10 * 60 * 1000;
        const elapsed = Date.now() - playStartTime;
        
        if (elapsed < durationMs) {
          audio.currentTime = 0;
          audio.play().catch(() => setIsPlaying(false));
        } else {
          setIsPlaying(false);
        }
      }
    };

    audio.addEventListener("ended", handleEnded);
    return () => audio.removeEventListener("ended", handleEnded);
  }, [loopMode, playStartTime]);

  const startPlayback = async () => {
    if (!audioRef.current || !generatedAudio) return;

    try {
      audioRef.current.src = generatedAudio.audio_url;
      audioRef.current.load();
      
      // Set loop for continuous mode
      audioRef.current.loop = loopMode === "continuous";
      
      // Track start time for timed modes
      if (loopMode === "5min" || loopMode === "10min") {
        setPlayStartTime(Date.now());
      }
      
      await audioRef.current.play();
      setIsPlaying(true);
    } catch (err) {
      console.log("Playback error:", err.message);
      setError("Unable to play audio. Try downloading instead.");
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current || !generatedAudio) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      startPlayback();
    }
  };

  const handleLoopModeChange = (newMode) => {
    setLoopMode(newMode);
    
    // Update loop setting on audio element if it exists
    if (audioRef.current) {
      audioRef.current.loop = newMode === "continuous";
      
      // Reset start time for timed modes
      if (newMode === "5min" || newMode === "10min") {
        setPlayStartTime(Date.now());
      }
    }
  };

  const handlePreviewBackground = async (key) => {
    // Check if this option has preview available
    if (!backgroundOptions[key]?.preview_available || key === "silence") {
      setPreviewingBg(null);
      if (bgAudioRef.current) bgAudioRef.current.pause();
      return;
    }

    if (previewingBg === key) {
      setPreviewingBg(null);
      if (bgAudioRef.current) bgAudioRef.current.pause();
    } else {
      // Stop any current preview
      if (bgAudioRef.current) {
        bgAudioRef.current.pause();
      }
      
      try {
        if (bgAudioRef.current) {
          // Use the preview API endpoint
          const previewUrl = `${API_URL}/api/voice-studio/background-audio/${key}/preview`;
          bgAudioRef.current.src = previewUrl;
          bgAudioRef.current.load(); // Important for iOS
          await bgAudioRef.current.play();
          setPreviewingBg(key);
        }
      } catch (err) {
        console.log("Audio preview not available:", err.message);
        // Still select it even if preview fails
        setPreviewingBg(null);
      }
    }
  };

  const handleDownload = () => {
    if (!generatedAudio?.audio_url) return;
    
    const link = document.createElement("a");
    link.href = generatedAudio.audio_url;
    link.download = `${generatedAudio.title || "installation"}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getAffirmationText = () => {
    return useCustom ? customText : selectedAffirmation?.text || "";
  };

  const canProceed = () => {
    switch (step) {
      case 1: return useCustom ? customText.trim().length > 0 : selectedAffirmation !== null;
      case 2: return selectedStyle !== null;
      case 3: return selectedBackground !== null;
      case 4: return selectedBinaural !== null;
      case 5: return true;
      default: return false;
    }
  };

  if (loading) {
    return (
      <PageWrapper showNav={false}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#4A6741] border-t-transparent rounded-full animate-spin" />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper showNav={false}>
      <div className="min-h-screen bg-[#FAFAF7]">
        {/* Hidden audio elements with iOS compatibility */}
        <audio 
          ref={audioRef} 
          onEnded={() => setIsPlaying(false)} 
          playsInline 
          preload="none"
        />
        <audio 
          ref={bgAudioRef} 
          loop 
          onEnded={() => setPreviewingBg(null)} 
          playsInline 
          preload="none"
          crossOrigin="anonymous"
        />

        {/* Header */}
        <div className="bg-gradient-to-b from-[#2D4A2A] to-[#3D5A34] text-white px-6 pt-6 pb-8">
          <div className="max-w-lg mx-auto">
            <button
              onClick={() => step === 7 ? navigate("/voice-studio") : navigate(-1)}
              className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            <h1 className="font-heading text-2xl mb-1">
              {duplicateMode ? "Duplicate Installation" : challengePreset ? `Install: ${challengePreset.challenge_title}` : "Create Installation"}
            </h1>
            
            {/* Progress indicator - now 5 steps */}
            {step <= 5 && (
              <div className="flex items-center gap-2 mt-4">
                {[1, 2, 3, 4, 5].map(s => (
                  <div
                    key={s}
                    className={`h-1.5 flex-1 rounded-full transition-colors ${
                      s <= step ? "bg-white" : "bg-white/30"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-6 max-w-lg mx-auto pb-32">
          {/* Error message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm"
              >
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step 1: Choose Affirmation */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Choose Your Message</h2>
                <p className="text-sm text-[#6B7280]">Select from our library or write your own</p>
              </div>

              {/* Toggle */}
              <div className="flex gap-2 p-1 bg-[#F3F4F6] rounded-xl">
                <button
                  onClick={() => setUseCustom(false)}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    !useCustom ? "bg-white shadow text-[#1F2937]" : "text-[#6B7280]"
                  }`}
                >
                  Library
                </button>
                <button
                  onClick={() => setUseCustom(true)}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    useCustom ? "bg-white shadow text-[#1F2937]" : "text-[#6B7280]"
                  }`}
                >
                  Write Your Own
                </button>
              </div>

              {useCustom ? (
                <div className="pb-4">
                  <textarea
                    ref={textareaRef}
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    onFocus={() => {
                      // Scroll textarea into view on iOS when keyboard opens
                      setTimeout(() => {
                        textareaRef.current?.scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center' 
                        });
                      }, 300);
                    }}
                    placeholder="I am confident and calm..."
                    rows={5}
                    className="w-full px-4 py-4 border border-[#E5E7EB] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A6741]/30 resize-none text-lg"
                    style={{ fontSize: '16px' }} 
                  />
                  <p className="text-xs text-[#9CA3AF] mt-2">
                    Start with "I am" or "I have" for best results
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {!selectedCategory ? (
                    <div className="space-y-2">
                      {categories.map(cat => (
                        <Card
                          key={cat.id}
                          className="p-4 cursor-pointer hover:shadow-md transition-all"
                          onClick={() => handleCategorySelect(cat)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Sparkles size={20} className="text-[#4A6741]" />
                              <span className="font-medium text-[#1F2937]">{cat.name}</span>
                            </div>
                            <ChevronRight size={18} className="text-[#9CA3AF]" />
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <button
                        onClick={() => { setSelectedCategory(null); setSelectedAffirmation(null); }}
                        className="text-sm text-[#4A6741] flex items-center gap-1"
                      >
                        <ArrowLeft size={16} />
                        Back to categories
                      </button>
                      
                      {affirmations.map(aff => (
                        <Card
                          key={aff.id}
                          className={`p-4 cursor-pointer transition-all ${
                            selectedAffirmation?.id === aff.id
                              ? "ring-2 ring-[#4A6741] bg-[#4A6741]/5"
                              : "hover:shadow-md"
                          }`}
                          onClick={() => setSelectedAffirmation(aff)}
                        >
                          <p className="text-[#1F2937] italic">"{aff.text}"</p>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {/* Step 2: Voice Style */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Select Voice Style</h2>
                <p className="text-sm text-[#6B7280]">Choose how your voice will sound</p>
              </div>

              <div className="space-y-3">
                {Object.entries(voiceStyles).map(([key, style]) => (
                  <Card
                    key={key}
                    className={`p-4 cursor-pointer transition-all ${
                      selectedStyle === key
                        ? "ring-2 ring-[#4A6741] bg-[#4A6741]/5"
                        : "hover:shadow-md"
                    }`}
                    onClick={() => setSelectedStyle(key)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedStyle === key ? "bg-[#4A6741] text-white" : "bg-[#F3F4F6] text-[#6B7280]"
                      }`}>
                        <Volume2 size={18} />
                      </div>
                      <div>
                        <h4 className="font-medium text-[#1F2937]">{style.name}</h4>
                        <p className="text-xs text-[#6B7280]">{style.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Background Audio */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Select Background</h2>
                <p className="text-sm text-[#6B7280]">Choose ambient audio to play with your voice</p>
              </div>

              <div className="space-y-3">
                {Object.entries(backgroundOptions).map(([key, bg]) => (
                  <Card
                    key={key}
                    className={`p-4 cursor-pointer transition-all ${
                      selectedBackground === key
                        ? "ring-2 ring-[#4A6741] bg-[#4A6741]/5"
                        : "hover:shadow-md"
                    }`}
                    onClick={() => setSelectedBackground(key)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        selectedBackground === key ? "bg-[#4A6741] text-white" : "bg-[#F3F4F6] text-[#6B7280]"
                      }`}>
                        <Volume2 size={18} />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-[#1F2937]">{bg.name}</h4>
                        <p className="text-xs text-[#6B7280]">{bg.description}</p>
                      </div>
                      {/* Preview button for available audio */}
                      {bg.preview_available && key !== "silence" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewBackground(key);
                          }}
                          className={`p-2 rounded-full transition-all ${
                            previewingBg === key 
                              ? "bg-[#4A6741] text-white" 
                              : "bg-[#F3F4F6] text-[#6B7280] hover:bg-[#E5E7EB]"
                          }`}
                          data-testid={`preview-bg-${key}`}
                        >
                          {previewingBg === key ? <Pause size={16} /> : <Play size={16} />}
                        </button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 4: Brainwave States (Binaural Beats) - NEW */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Brainwave State</h2>
                <p className="text-sm text-[#6B7280]">Optional binaural beats for deeper effect</p>
              </div>

              {/* Headphones notice */}
              {selectedBinaural !== "none" && (
                <Card className="p-4 bg-amber-50 border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Headphones size={20} className="text-amber-600" />
                    <p className="text-sm text-amber-800">
                      Use headphones for best binaural beat experience
                    </p>
                  </div>
                </Card>
              )}

              <div className="space-y-3">
                {Object.entries(binauralOptions).map(([key, beat]) => {
                  const IconComponent = BINAURAL_ICONS[key] || Brain;
                  return (
                    <Card
                      key={key}
                      className={`p-4 cursor-pointer transition-all ${
                        selectedBinaural === key
                          ? "ring-2 ring-[#4A6741] bg-[#4A6741]/5"
                          : "hover:shadow-md"
                      }`}
                      onClick={() => setSelectedBinaural(key)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          selectedBinaural === key ? "bg-[#4A6741] text-white" : "bg-[#F3F4F6] text-[#6B7280]"
                        }`}>
                          <IconComponent size={18} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-[#1F2937]">{beat.name}</h4>
                          <p className="text-xs text-[#6B7280]">{beat.description}</p>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 5: Review */}
          {step === 5 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Review Your Installation</h2>
                <p className="text-sm text-[#6B7280]">Ready to create your personalized audio</p>
              </div>

              <Card className="p-6 space-y-4">
                <div>
                  <p className="text-xs text-[#9CA3AF] mb-1">Affirmation</p>
                  <p className="text-[#1F2937] italic">"{getAffirmationText()}"</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#9CA3AF] mb-1">Voice Style</p>
                    <p className="text-[#1F2937] font-medium">{voiceStyles[selectedStyle]?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#9CA3AF] mb-1">Background</p>
                    <p className="text-[#1F2937] font-medium">{backgroundOptions[selectedBackground]?.name}</p>
                  </div>
                </div>
                {selectedBinaural !== "none" && (
                  <div>
                    <p className="text-xs text-[#9CA3AF] mb-1">Brainwave State</p>
                    <p className="text-[#1F2937] font-medium">{binauralOptions[selectedBinaural]?.name}</p>
                  </div>
                )}
              </Card>

              <Button onClick={handleGenerate} className="w-full py-4">
                <Sparkles size={20} />
                Create My Installation
              </Button>
            </motion.div>
          )}

          {/* Step 6: Generating */}
          {step === 6 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-12 text-center space-y-6"
            >
              <div className="w-20 h-20 border-4 border-[#4A6741]/20 border-t-[#4A6741] rounded-full animate-spin mx-auto" />
              <div>
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">Creating Your Installation</h2>
                <p className="text-sm text-[#6B7280]">Generating your personalized audio...</p>
              </div>
            </motion.div>
          )}

          {/* Step 7: Complete */}
          {step === 7 && generatedAudio && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6"
            >
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 bg-[#4A6741]/10 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <Check size={40} className="text-[#4A6741]" />
                </motion.div>
                <h2 className="font-heading text-xl text-[#1F2937] mb-2">
                  Your Installation Is Ready
                </h2>
                <p className="text-sm text-[#6B7280]">
                  Listen, save, and transform
                </p>
              </div>

              <Card className="p-6">
                <p className="text-[#1F2937] italic text-center mb-6">
                  "{generatedAudio.affirmation_text}"
                </p>

                {/* Play controls */}
                <div className="flex items-center justify-center gap-4 mb-6">
                  <button
                    onClick={togglePlayback}
                    className="w-16 h-16 bg-[#4A6741] rounded-full flex items-center justify-center text-white shadow-lg"
                    data-testid="play-installation-btn"
                  >
                    {isPlaying ? <Pause size={28} /> : <Play size={28} className="ml-1" />}
                  </button>
                </div>

                {/* Binaural notice */}
                {generatedAudio.binaural_beat !== "none" && (
                  <div className="flex items-center justify-center gap-2 mb-4 text-sm text-[#6B7280]">
                    <Headphones size={16} />
                    <span>Use headphones for {generatedAudio.binaural_beat_name}</span>
                  </div>
                )}

                {/* Loop options */}
                <div className="mb-6">
                  <p className="text-xs text-[#9CA3AF] text-center mb-2">Loop Mode</p>
                  <div className="flex gap-2 justify-center flex-wrap">
                    {[
                      { id: "single", label: "Single" },
                      { id: "5min", label: "5 min" },
                      { id: "10min", label: "10 min" },
                      { id: "continuous", label: "Continuous" }
                    ].map(mode => (
                      <button
                        key={mode.id}
                        onClick={() => handleLoopModeChange(mode.id)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          loopMode === mode.id
                            ? "bg-[#4A6741] text-white"
                            : "bg-[#F3F4F6] text-[#6B7280]"
                        }`}
                        data-testid={`loop-mode-${mode.id}`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={handleDownload} className="flex-1">
                    <Download size={18} />
                    Download
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowShareModal(true)} 
                    className="flex-1"
                    data-testid="share-installation-btn"
                  >
                    <Share2 size={18} />
                    Share
                  </Button>
                </div>

                <Button onClick={() => navigate("/voice-studio?tab=installations")} className="w-full">
                  <Check size={18} />
                  Done
                </Button>
              </Card>

              <Button
                variant="ghost"
                onClick={() => { setStep(1); setGeneratedAudio(null); }}
                className="w-full"
              >
                <RotateCcw size={18} />
                Create Another
              </Button>
            </motion.div>
          )}

          {/* Navigation buttons */}
          {step >= 1 && step <= 5 && (
            <div className="flex gap-3 mt-8">
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
                  <ArrowLeft size={18} />
                  Back
                </Button>
              )}
              {step < 5 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="flex-1"
                >
                  Next
                  <ArrowRight size={18} />
                </Button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* Share Installation Modal */}
      <ShareInstallationCard
        installation={generatedAudio}
        isVisible={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </PageWrapper>
  );
};

export default CreateInstallationPage;
